// Browser-side Ed25519 signing for the offline-bundle push flow. Uses
// WebCrypto's Ed25519 support (Chrome 113+, Firefox 130+, Safari 17+). The
// canonicalization MUST match server/atoms.js — otherwise the bytes the
// server hashes for atom_hash won't equal the bytes the browser signed.
//
// This is a TypeScript port of canonicalize/atomHash from server/atoms.js;
// the two implementations are kept in lockstep by the test that round-trips
// a CLI-signed bundle through the server's verify path.

export type AtomDraft =
  | { type: 'file'; name: string; src: string; sha256: string; dest: string; mode: string; owner: string; size: number; mime: string }
  | { type: 'action'; name: string; when?: string; trigger?: string[]; command: string }
  | { type: 'agent'; file: string; sha256: string }
  | {
      type: 'assembly';
      client_uuid: string;
      files: string[];
      actions: string[];
      agent: { script: string; verifier: string; service: string; timer: string };
      expires_at?: number;
    };

export type AtomDrafts = {
  files: AtomDraft[];
  actions: AtomDraft[];
  agents: AtomDraft[];
  assemblies: AtomDraft[];
};

export type SignedAtom = {
  atom_json: string;
  signature: string;
  key_id: string;
};

export type SignedBundle = {
  atoms: SignedAtom[];
  assemblies: Record<string, string>;
};

export function canonicalize(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(canonicalize).join(',') + ']';
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalize(obj[k])).join(',') + '}';
}

async function sha256Hex(s: string): Promise<string> {
  const buf = new TextEncoder().encode(s);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function atomHash(atomObj: unknown): Promise<string> {
  return sha256Hex(canonicalize(atomObj));
}

function pemToDer(pem: string): ArrayBuffer {
  const body = pem
    .replace(/-----BEGIN [A-Z ]+-----/, '')
    .replace(/-----END [A-Z ]+-----/, '')
    .replace(/\s+/g, '');
  const bin = atob(body);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function b64encode(bytes: ArrayBuffer): string {
  const u = new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]);
  return btoa(s);
}

async function importEd25519Private(pem: string): Promise<CryptoKey> {
  const der = pemToDer(pem);
  return crypto.subtle.importKey('pkcs8', der, { name: 'Ed25519' } as AlgorithmIdentifier, false, ['sign']);
}

async function deriveKeyIdFromPrivate(pem: string): Promise<string> {
  // WebCrypto doesn't expose "give me the SPKI of this private key" directly.
  // Workaround: re-import as private, then we can't extract the public part
  // through standard APIs in all browsers. So derive key_id by trusting the
  // server's pinned key_id when we get it from /unsigned. The signer doesn't
  // need to compute key_id from the private key itself.
  // This helper is kept for symmetry / future use; callers should pass the
  // server-provided key_id through to signBundle.
  return '';
}

async function signAtom(privKey: CryptoKey, atomObj: unknown, keyId: string): Promise<SignedAtom> {
  const atom_json = canonicalize(atomObj);
  const sigBuf = await crypto.subtle.sign(
    { name: 'Ed25519' },
    privKey,
    new TextEncoder().encode(atom_json),
  );
  return { atom_json, signature: b64encode(sigBuf), key_id: keyId };
}

export async function signBundle(
  pem: string,
  drafts: AtomDrafts,
  expiresAt: number | null,
  pinnedKeyId: string,
): Promise<SignedBundle> {
  const priv = await importEd25519Private(pem);

  const atoms: SignedAtom[] = [];
  for (const f of drafts.files) atoms.push(await signAtom(priv, f, pinnedKeyId));
  for (const a of drafts.actions) atoms.push(await signAtom(priv, a, pinnedKeyId));
  for (const ag of drafts.agents) atoms.push(await signAtom(priv, ag, pinnedKeyId));

  const assemblies: Record<string, string> = {};
  for (const asm of drafts.assemblies) {
    if (asm.type !== 'assembly') continue;
    const withExpiry = expiresAt != null ? { ...asm, expires_at: expiresAt } : asm;
    atoms.push(await signAtom(priv, withExpiry, pinnedKeyId));
    assemblies[withExpiry.client_uuid] = await atomHash(withExpiry);
  }

  return { atoms, assemblies };
}

// Unused export kept for tree-shaking-safe re-export from Signing.tsx.
void deriveKeyIdFromPrivate;
